/**
 * Parse a `/g/{gid}/{token}/` detail page into a {@link ComicDetails}.
 *
 * The detail HTML is stable enough that EhViewer parses it with a handful
 * of well-known selectors:
 *
 *   - `h1#gn` — translated title
 *   - `h1#gj` — original / Japanese title
 *   - `#gd1 img` — cover
 *   - `#gdc div` — category text
 *   - `#gdn a` — uploader name
 *   - `#gdd tr` rows for length / posted / language / file size / etc.
 *   - `#taglist tr` for tags, grouped by namespace (`tr.td:first-child` is
 *     `namespace:`, `td:last-child a div.gt` for each tag)
 *   - `#gdr` rating
 *
 * We capture the URL → gid/token via the canonical `<link rel="canonical">`
 * tag — that survives a layout reshuffle better than relying on URL inputs.
 */

import { parseDocument, selectOrThrow, textOf, toInt, absoluteUrl } from '@/lib/html';
import { ParseError } from '@/lib/errors';
import type { ComicDetails, SourceComment, SourceId, Tag } from '@/sources/types';
import { publicEhImageUrl, SITE_E, SITE_EX, type SiteContext } from '../urls';

export interface GalleryDetailContext {
  source: SourceId;
  /** The URL we fetched, used as base for relative cover links. */
  pageUrl: string;
}

const GALLERY_URL_RE = /\/g\/(\d+)\/([0-9a-z]+)/i;

function readGdd(doc: Document): {
  length?: number;
  posted?: number;
  language?: string;
  fileSize?: string;
} {
  const out: ReturnType<typeof readGdd> = {};
  const rows = Array.from(doc.querySelectorAll('#gdd tr'));
  for (const row of rows) {
    const tds = row.querySelectorAll('td');
    if (tds.length < 2) continue;
    const left = textOf(tds[0]).toLowerCase().replace(/:$/, '').trim();
    const right = textOf(tds[1]).trim();
    if (!left || !right) continue;
    if (left === 'length') {
      out.length = toInt(right.match(/\d+/)?.[0] ?? '0');
    } else if (left === 'posted') {
      const ms = Date.parse(right);
      if (Number.isFinite(ms)) out.posted = ms;
    } else if (left === 'language') {
      out.language = right
        .replace(/\s*translated$/i, '')
        .trim()
        .toLowerCase();
    } else if (left === 'file size') {
      out.fileSize = right;
    }
  }
  return out;
}

function readTags(doc: Document): Tag[] {
  const tags: Tag[] = [];
  const rows = Array.from(doc.querySelectorAll('#taglist tr'));
  for (const row of rows) {
    const tds = row.querySelectorAll('td');
    if (tds.length < 2) continue;
    let namespace = textOf(tds[0]).replace(/:$/, '').trim().toLowerCase();
    if (!namespace) namespace = 'misc';
    const anchors = Array.from(tds[1]?.querySelectorAll('a') ?? []) as HTMLAnchorElement[];
    for (const a of anchors) {
      const inner = a.querySelector('div.gt, div.gtl, div.gtw');
      const name = textOf(inner ?? a).trim();
      if (!name) continue;
      const href = a.getAttribute('href') ?? '';
      const tagSlug = href.match(/\/tag\/(.+)$/)?.[1] ?? `${namespace}:${name}`;
      tags.push({
        id: decodeURIComponent(tagSlug),
        name,
        namespace,
      });
    }
  }
  return tags;
}

function normalizeCommentText(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/[ \t\r\f\v]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
    .trim();
}

function textWithBreaks(node: Node): string {
  if (node.nodeType === 3) return node.textContent ?? '';
  if (node.nodeType !== 1) return '';

  const el = node as Element;
  if (el.tagName.toLowerCase() === 'br') return '\n';

  const childText = Array.from(el.childNodes).map(textWithBreaks).join('');
  if (/^(?:div|p|blockquote|li)$/i.test(el.tagName)) return `${childText}\n`;
  return childText;
}

function directChildByClass(container: Element, className: string): Element | null {
  return Array.from(container.children).find((child) => child.classList.contains(className)) ?? null;
}

function readCommentId(comment: Element, index: number, bodyNode?: Element | null): { id: string; rawId?: string } {
  const rawId =
    bodyNode?.getAttribute('id') ??
    comment.getAttribute('id') ??
    comment.querySelector('[id^="comment_"]:not([id^="comment_score_"]), [id^="c"]:not([id^="cvotes_"])')?.getAttribute('id') ??
    '';
  const m = /^(?:comment_|c)?(\d+)$/i.exec(rawId);
  if (m?.[1]) return { id: m[1], rawId };
  return { id: `index-${index + 1}` };
}

function readCommentDate(headerText: string): number | undefined {
  const m = /posted\s+on\s+(.+?)\s+by\s*:/i.exec(headerText);
  const rawDate = m?.[1]?.trim();
  if (!rawDate) return undefined;
  const hasTimezone = /\b(?:UTC|GMT|[A-Z]{2,4})\b|[+-]\d{2}:?\d{2}\b/.test(rawDate);
  const ms = Date.parse(hasTimezone ? rawDate : `${rawDate} UTC`);
  return Number.isFinite(ms) ? ms : undefined;
}

function readCommentAuthor(header: Element | null, headerText: string): string {
  const anchors = Array.from(header?.querySelectorAll('a') ?? []);
  for (const anchor of anchors.reverse()) {
    const text = textOf(anchor).trim();
    if (text) return text;
  }
  const named = textOf(header?.querySelector('.chn, .c7')).trim();
  if (named) return named;
  const m = /\bby\s*:\s*(.+)$/i.exec(headerText);
  return m?.[1]?.trim() || 'Unknown';
}

function readCommentScore(comment: Element): number | undefined {
  const header = directChildByClass(comment, 'c2');
  const scoreNode =
    (header ? directChildByClass(header, 'c5') : null) ??
    (header ? directChildByClass(header, 'c4') : null) ??
    (header ? directChildByClass(header, 'cs') : null) ??
    directChildByClass(comment, 'c5') ??
    directChildByClass(comment, 'c4') ??
    directChildByClass(comment, 'cs');
  const scoreText = textOf(scoreNode);
  const m = /([+-]\s*\d+)|(?:score\s*:?\s*(-?\d+))/i.exec(scoreText);
  const raw = m?.[1] ?? m?.[2];
  if (!raw) return undefined;
  const score = Number.parseInt(raw.replace(/\s+/g, ''), 10);
  return Number.isFinite(score) ? score : undefined;
}

function readCommentAvatar(header: Element | null, pageUrl: string): string | undefined {
  const img = header?.querySelector('img[src]') as HTMLImageElement | null;
  const src = img?.getAttribute('src')?.trim();
  return src ? absoluteUrl(src, pageUrl) : undefined;
}

function readGid(doc: Document, fallbackUrl: string): { gid: string; token: string } {
  // Try canonical link first.
  const canonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? '';
  const direct = GALLERY_URL_RE.exec(canonical) ?? GALLERY_URL_RE.exec(fallbackUrl);
  if (direct) return { gid: direct[1] ?? '', token: direct[2] ?? '' };

  // Fallback: any anchor that looks like /g/N/T/, prefer the title.
  const link =
    (doc.querySelector('a[href*="/g/"][href$="/"]') as HTMLAnchorElement | null)?.getAttribute(
      'href',
    ) ?? '';
  const m = GALLERY_URL_RE.exec(link);
  if (m) return { gid: m[1] ?? '', token: m[2] ?? '' };
  throw new ParseError('parseGalleryDetail: could not determine gid/token');
}

function siteFromContext(source: SourceId): SiteContext {
  return source === 'exhentai' ? SITE_EX : SITE_E;
}

function readCover(doc: Document, ctx: GalleryDetailContext): string {
  const img = doc.querySelector('#gd1 img, #gd1 > div > img') as HTMLImageElement | null;
  const src = img?.getAttribute('src');
  if (src) return publicEhImageUrl(absoluteUrl(src, ctx.pageUrl), siteFromContext(ctx.source));
  const bg = doc.querySelector('#gd1 > div') as HTMLElement | null;
  const inlineStyle = bg?.getAttribute('style') ?? '';
  const urlMatch = /url\((['"]?)(.*?)\1\)/.exec(inlineStyle);
  if (!urlMatch?.[2]) return '';
  return publicEhImageUrl(absoluteUrl(urlMatch[2], ctx.pageUrl), siteFromContext(ctx.source));
}

function readRating(doc: Document): number | undefined {
  const ratingTd = doc.querySelector('#rating_label');
  if (!ratingTd) return undefined;
  const txt = textOf(ratingTd);
  const m = /([\d.]+)/.exec(txt);
  if (!m || !m[1]) return undefined;
  const n = Number.parseFloat(m[1]);
  return Number.isFinite(n) ? n : undefined;
}

function readCategory(doc: Document): string {
  const cat = doc.querySelector('#gdc div, #gdc > .cn, #gdc a');
  return textOf(cat).trim();
}

function readUploader(doc: Document): string {
  const a = doc.querySelector('#gdn a');
  return textOf(a).trim();
}

function readTitles(doc: Document): { title: string; altTitle?: string } {
  const main = textOf(selectOrThrow(doc, 'h1#gn', { source: 'eh' })).trim();
  const alt = textOf(doc.querySelector('h1#gj')).trim();
  if (alt && alt !== main) return { title: main, altTitle: alt };
  return { title: main };
}

export function parseGalleryDetail(html: string, ctx: GalleryDetailContext): ComicDetails {
  if (!html) throw new ParseError('parseGalleryDetail: empty html', { source: ctx.source });
  const doc = parseDocument(html);

  const { gid, token } = readGid(doc, ctx.pageUrl);
  const { title, altTitle } = readTitles(doc);
  const cover = readCover(doc, ctx);
  const category = readCategory(doc);
  const uploader = readUploader(doc);
  const meta = readGdd(doc);
  const rating = readRating(doc);
  const tags = readTags(doc);

  // Some galleries don't expose a numeric length until you scrape an api.php
  // call; default to 0 so callers know to call `gdata`.
  const pageCount = meta.length ?? 0;

  const detail: ComicDetails = {
    id: `${gid}:${token}`,
    source: ctx.source,
    title,
    tags,
    coverUrl: cover,
    pageCount,
    uploader,
  };
  if (altTitle) detail.altTitle = altTitle;
  if (rating !== undefined) detail.rating = rating;
  if (meta.language) detail.language = meta.language;
  if (meta.posted !== undefined) detail.uploadedAt = meta.posted;
  if (category) {
    // Categories are exposed as plain text on detail pages; we stash on the
    // first tag-like slot under namespace `category` for blacklist filtering.
    detail.tags.unshift({
      id: `category:${category.toLowerCase()}`,
      name: category,
      namespace: 'category',
    });
  }
  if (rating !== undefined) {
    // Keep the historical tag exposure for blacklist/search compatibility.
    detail.tags.push({ id: `rating:${rating}`, name: String(rating), namespace: 'rating' });
  }
  // `token` survives via tags as well so favorites can rebuild the URL later
  // without re-fetching.
  detail.tags.push({ id: `token:${token}`, name: token, namespace: 'token' });

  return detail;
}

export function parseGalleryComments(
  html: string,
  ctx: { source: SourceId; pageUrl?: string },
): SourceComment[] {
  if (!html) throw new ParseError('parseGalleryComments: empty html', { source: ctx.source });
  const doc = parseDocument(html);
  const containers = Array.from(doc.querySelectorAll('#cdiv .c1, .c1[id^="comment_"], .c1[id^="c"]'));
  const comments: SourceComment[] = [];

  containers.forEach((container, index) => {
    const bodyNode = directChildByClass(container, 'c6') ?? directChildByClass(container, 'c3');
    const body = bodyNode ? normalizeCommentText(textWithBreaks(bodyNode)) : '';
    if (!body) return;

    const header = directChildByClass(container, 'c2');
    const headerText = textOf(header);
    const { id, rawId } = readCommentId(container, index, bodyNode);
    const comment: SourceComment = {
      id,
      authorName: readCommentAuthor(header, headerText),
      body,
    };

    const createdAt = readCommentDate(headerText);
    if (createdAt !== undefined) comment.createdAt = createdAt;

    const score = readCommentScore(container);
    if (score !== undefined) comment.score = score;

    const avatar = readCommentAvatar(header, ctx.pageUrl ?? '');
    if (avatar !== undefined) comment.authorAvatarUrl = avatar;

    if (rawId) comment.extra = { rawId };
    comments.push(comment);
  });

  return comments;
}
